import { MeetingChatAdapter } from "@kalyzee/kast-web-sdk";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { Chat, ChatAvatar, ChatMessageData, ChatRef, Loading, useStateWithRef, WithNativeHTMLProps } from "@kalyzee/kast-web-sdk/web-components";
import { Chatroom, ChatroomStatus } from "@kalyzee/kast-web-sdk/websocket";
import React, { ForwardedRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ReactComponent as IconChatDisabled } from "../../assets/icons/noChat.svg";
import { useMeetingSelector } from "../../hooks/meeting";
import "./Chat.css";
import styles from "./Chat.module.css";

export interface DashboardChatProps extends WithNativeHTMLProps<HTMLDivElement> {
  chatroom: Chatroom;
  chatAdapter?: MeetingChatAdapter;
  meetingAPI?: MeetingContainerAPI;
  className?: string;
  style?: React.CSSProperties;
  hideHeader?: boolean;
  hideActiveChatButton?: boolean;
  hideClearChatButton?: boolean;
  hideSendBar?: boolean;
  disableReactions?: boolean;
  disableDeletion?: boolean;
  inactive?: boolean;
  onUnreadMessages?: (messages: ChatMessageData[]) => void;
  onClick?: (message: ChatMessageData) => void;
}

export interface DashboardChatRef {
  clearMessages: () => void;
}

const DashboardChat = React.forwardRef(
  (
    {
      chatroom,
      chatAdapter,
      meetingAPI,
      className,
      style,
      nativeProps,
      hideHeader = false,
      hideActiveChatButton = false,
      hideClearChatButton = false,
      hideSendBar = false,
      disableReactions = false,
      disableDeletion = true,
      inactive,
      onUnreadMessages,
      onClick,
    }: DashboardChatProps,
    forwardRef: ForwardedRef<DashboardChatRef>
  ) => {
    const [chatStatus, setChatStatus] = useState<ChatroomStatus | "loading" | undefined>();
    const [chatIsReady, setChatIsReady, chatIsReadyRef] = useStateWithRef(false);
    const [meeting] = useMeetingSelector(meetingAPI, (state) => state.meeting);
    const chatRef = useRef<ChatRef>(null);

    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    useEffect(() => {
      if (!chatAdapter) return;
      const stateListener = chatAdapter.addEventListener("state", (state) => setChatStatus(state));
      const readyListener = chatAdapter.addEventListener("ready", () => {
        if (!chatIsReadyRef.current) setChatIsReady(true);
      });
      if (chatAdapter.ready && !chatIsReadyRef.current) setChatIsReady(true);
      return () => {
        stateListener.flush();
        readyListener.flush();
      };
    }, [chatAdapter]);

    useImperativeHandle(forwardRef, () => ({
      clearMessages: () => {
        chatAdapter?.archiveMessages();
      },
    }), [chatAdapter]);

    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    if (!meeting || !chatAdapter) return null;

    const chatIsDisabled = () => {
      if (chatStatus) return chatStatus === ChatroomStatus.disabled;
      return chatroom?.status === ChatroomStatus.disabled;
    };

    const classes = [styles.container, "dashboard_chat"];
    if (hideSendBar) classes.push("hide_send_bar");
    if (className) classes.push(className);

    return (
      <div {...(nativeProps ?? {})} className={classes.join(" ")} style={style}>
        <div className={styles.content}>
          {chatIsReady ? (
            <>
              {chatroom ? (
                <>
                  <Chat
                    className={styles.chat}
                    ref={chatRef}
                    adapter={chatAdapter}
                    disableLoadMoreMessages
                    hideAvatarMaxRepetition={0}
                    disabled={chatIsDisabled()}
                    messageParameters={{
                      disableBubbleInteraction: disableReactions && disableDeletion,
                      disableDeletion: disableDeletion,
                      disableReaction: disableReactions,
                      disableAvatarInteraction: !onClick,
                      onAvatarClick: (msg) => {
                        onClick?.(msg);
                        return false;
                      },
                      onCustomValidationForDeletion: (msg) => true,
                    }}
                    sendBarProps={{
                      showIndicator: true,
                      hideIndicatorIfEmpty: true,
                      limit: 250,
                    }}
                    callbacks={{
                      unreadMessages: onUnreadMessages,
                    }}
                    inactive={inactive}
                    onCustomRenderUsername={(msg, _, username) => {
                      return (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          {/* {isUserFromPedagogy(msg.user.id) ? <PedagogyUtils.HeaderChatMessage /> : null} */}
                          <div style={{ display: "flex", gap: "0.5em", alignItems: "center" }}>
                            <ChatAvatar 
                              image={msg.user.avatar} 
                              username={msg.user.username} 
                              style={{ width: "2em", height: "2em" }}
                            />
                            <div>{username}</div>
                          </div>
                        </div>
                      );
                    }}
                  />

                  {chatIsDisabled() && (
                    <div className={styles.overlayDisabled}>
                      <IconChatDisabled width={40} height={40} />
                    </div>
                  )}
                  {chatStatus === "loading" && (
                    <Loading style={{ width: "100%", height: "100%", backgroundColor: "#00000022", position: "absolute", top: "0px", left: "0px" }} />
                  )}
                </>
              ) : (
                <div>Le chat n'est pas disponible</div>
              )}
            </>
          ) : (
            <Loading style={{ width: "100%", height: "100%" }} />
          )}
        </div>
      </div>
    );
  }
);

export default React.memo(DashboardChat);
